import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Document } from '../../../core/models/policy';
import { UiHelperService } from '../../../core/services/core/ui-helper.service';
import { FileHelperService } from '../../../core/services/helpers/file-helper.service';
import { Platform, ActionSheetController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Camera } from '@ionic-native/camera/ngx';

@Component({
  selector: 'app-select-file',
  templateUrl: './select-file.component.html',
  styleUrls: ['./select-file.component.scss']
})
export class SelectFileComponent {
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;

  @Input() disabled = false;
  @Input() useDefaultNaming = true;
  @Input() buttonText = 'SELECT_FILE.BUTTON_SELECT_DOCUMENT';
  @Input() textTitle = '';
  @Input() textSubtitle = '';
  @Input() textInfo = '';

  @Output() documentSelected = new EventEmitter<Document>();
  @Output() documentError = new EventEmitter<any>();
  @Output() documentCancel = new EventEmitter<any>();
  @Output() documentDropped = new EventEmitter<any>();

  constructor(
    private fileHelper: FileHelperService,
    private platform: Platform,
    private trans: TranslateService,
    private actionSheetCtrl: ActionSheetController,
    private ui: UiHelperService,
    private camera: Camera
  ) {}

  onOpenAddFileSelector(event: any) {
    if (this.platform.is('ios') || !this.platform.is('cordova')) {
      this.openNativeHtmlSelector();
    } else {
      this.openAddFilePopover(event);
    }
  }

  private async openAddFilePopover(event: any) {
    if (event) {
      event.stopPropagation();
    }
    const actionSheet = await this.actionSheetCtrl.create({
      buttons: [
        {
          text: this.trans.instant('DOCUMENTS.POPOVER.TAKE_PHOTO'),
          handler: () => {
            this.onTakePhoto();
          }
        },
        {
          text: this.trans.instant('DOCUMENTS.POPOVER.OPEN_WITH'),
          handler: () => {
            this.openNativeHtmlSelector();
          }
        },
        {
          text: this.trans.instant('CANCEL'),
          handler: () => {}
        }
      ]
    });
    actionSheet.present();
  }

  onTakePhoto() {
    // Android only
    const options = {
      destinationType: this.camera.DestinationType.DATA_URL,
      sourceType: this.camera.PictureSourceType.CAMERA,
      allowEdit: false,
      saveToPhotoAlbum: false,
      encodingType: this.camera.EncodingType.JPEG,
      correctOrientation: true
    };
    this.camera
      .getPicture(options)
      .then(async base64 => {
        await this.ui.presentLoading('app-select-file-component');
        this.fileHelper
          .getDocumentFromBase64(
            base64,
            'image/jpeg',
            this.useDefaultNaming
              ? this.fileHelper.generateFileName({ mimeType: 'image/jpeg' })
              : ''
          )
          .then(document => {
            this.ui.dismissLoading('app-select-file-component');
            this.emitDocumentSelected(document);
          })
          .catch(err => {
            this.ui.dismissLoading('app-select-file-component');
            this.emitDocumentError(err);
          });
      })
      .catch(err => {
        if (err === 'No Image Selected') {
          this.emitDocumentCancel(err);
          return;
        }
        this.emitDocumentError(err);
      });
  }

  private openNativeHtmlSelector() {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  async onSelectFile(event: any) {
    let file: File;
    if (event) {
      event.stopPropagation();
      file = event.target.files[0];
    }
    if (!file) {
      return;
    }
    await this.ui.presentLoading('app-select-file-component');
    this.fileHelper
      .fileToPolicyDocument(file)
      .then((document: Document) => {
        this.ui.dismissLoading('app-select-file-component');
        this.emitDocumentSelected(document);
      })
      .catch(err => {
        this.ui.dismissLoading('app-select-file-component');
        this.emitDocumentError(err);
      });
    event.target.value = null;
  }

  private emitDocumentSelected(document: Document) {
    this.documentSelected.emit(document);
  }

  private emitDocumentCancel(reason?: any) {
    this.documentCancel.emit(reason);
  }

  private emitDocumentError(err?) {
    if (err === 'No camera available') {
      this.ui.presentAlert('CANT_OPEN_CAMERA', 'INFO');
      return;
    }
    this.documentError.emit(err);
  }

  private emitDocumentDropped(document) {
    this.documentDropped.emit(document);
  }

  onDragEnter(event) {
    event.preventDefault();
  }

  onDrop(event) {
    const files = event.dataTransfer && event.dataTransfer.files;
    const fileSelected = files[0];
    const reader = new FileReader();

    reader.readAsDataURL(fileSelected);
    reader.onload = (_event) => {
      const document = {
        name: fileSelected.name,
        mimeType: fileSelected.type,
        size: fileSelected.size,
        base64Content: reader.result,
      };

      this.emitDocumentDropped(document);
    };

    event.preventDefault();
  }
}
